// Checks on the RAW input, before structural parsing:
//  - plain JSON only (no functions, symbols, class instances, getters on
//    exotic prototypes, cycles, non-finite numbers), bounded depth and size;
//  - schema version dispatch (an unknown version is reported, never guessed);
//  - PII guard: explicit, readable errors for attempts to enable personal-data
//    collection, which initial V1 does not support (ADR-0018 §2.1-7). The
//    structural layer rejects the same input; this adds the intent-level code.

import { childPath, isPlainObject } from "../schema/dsl.js";
import { CREATIVE_DOCUMENT_KIND, CREATIVE_DOCUMENT_SCHEMA_VERSION } from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { ENGINE_LIMITS } from "../limits.js";

/** Returns false (with an issue) if `input` is not bounded plain JSON. */
export function checkPlainJson(input: unknown, sink: IssueSink): boolean {
  const stack = new Set<object>();
  let nodes = 0;
  const visit = (value: unknown, path: string, depth: number): boolean => {
    nodes += 1;
    if (nodes > 200_000) {
      sink.add("DOCUMENT_TOO_LARGE", "$", "Document has too many nodes.", {});
      return false;
    }
    if (depth > ENGINE_LIMITS.maxDepth) {
      sink.add("TOO_DEEP", path, `Nesting deeper than ${ENGINE_LIMITS.maxDepth} levels.`, {
        max: ENGINE_LIMITS.maxDepth,
      });
      return false;
    }
    if (value === null || typeof value === "boolean" || typeof value === "string") return true;
    if (typeof value === "number") {
      if (Number.isFinite(value)) return true;
      sink.add("NOT_PLAIN_JSON", path, "Numbers must be finite.", {});
      return false;
    }
    if (typeof value !== "object") {
      sink.add("NOT_PLAIN_JSON", path, "Only JSON values are allowed.", { received: typeof value });
      return false;
    }
    if (stack.has(value)) {
      sink.add("NOT_PLAIN_JSON", path, "Cyclic structures are not allowed.", {});
      return false;
    }
    stack.add(value);
    let ok = true;
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length && ok; i++) ok = visit(value[i], childPath(path, i), depth + 1);
    } else if (isPlainObject(value)) {
      for (const key of Object.keys(value)) {
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        if (!descriptor || "get" in descriptor || "set" in descriptor) {
          sink.add("NOT_PLAIN_JSON", childPath(path, key), "Accessor properties are not allowed.", {});
          ok = false;
          break;
        }
        ok = visit(descriptor.value, childPath(path, key), depth + 1);
        if (!ok) break;
      }
    } else {
      sink.add("NOT_PLAIN_JSON", path, "Only plain objects and arrays are allowed.", {});
      ok = false;
    }
    stack.delete(value);
    return ok;
  };
  if (!visit(input, "$", 0)) return false;
  const bytes = new TextEncoder().encode(JSON.stringify(input)).length;
  if (bytes > ENGINE_LIMITS.maxDocumentBytes) {
    sink.add("DOCUMENT_TOO_LARGE", "$", `Serialized document exceeds ${ENGINE_LIMITS.maxDocumentBytes} bytes.`, {
      max: ENGINE_LIMITS.maxDocumentBytes,
      actual: bytes,
    });
    return false;
  }
  return true;
}

/** Version dispatch: v2 is the only version this engine validates. */
export function checkSchemaVersion(input: unknown, sink: IssueSink): boolean {
  if (!isPlainObject(input)) {
    sink.add("INVALID_TYPE", "$", "Expected object.", { expected: "object" });
    return false;
  }
  if (input.kind !== CREATIVE_DOCUMENT_KIND) {
    sink.add("INVALID_VALUE", "$.kind", `Must be "${CREATIVE_DOCUMENT_KIND}".`, { expected: CREATIVE_DOCUMENT_KIND });
    return false;
  }
  if (input.schemaVersion !== CREATIVE_DOCUMENT_SCHEMA_VERSION) {
    sink.add("UNSUPPORTED_SCHEMA_VERSION", "$.schemaVersion", "Unsupported Creative Document schema version.", {
      supported: CREATIVE_DOCUMENT_SCHEMA_VERSION,
      ...(typeof input.schemaVersion === "number" ? { received: input.schemaVersion } : {}),
    });
    return false;
  }
  return true;
}

const PII_CONTROLS = new Set([
  "email",
  "phone",
  "tel",
  "name",
  "fullname",
  "address",
  "postalcode",
  "date",
  "birthdate",
  "id",
  "document",
  // 7.6E: hidden identifiers, credentials, payment and uploads are never creative-form controls.
  "hidden",
  "password",
  "file",
  "upload",
  "payment",
  "card",
  "creditcard",
  "iban",
  "account",
  "url",
  "signature",
  "location",
]);
const FREE_TEXT_SURVEY_KINDS = new Set([
  "text",
  "textarea",
  "open",
  "free_text",
  "freetext",
  "other",
  "email",
  "phone",
]);
const FREE_TEXT_OPTION_KEYS = ["allowOther", "freeText", "otherText", "allowFreeText"];

/** Intent-level PII detection on raw input (structure rejects the same shapes generically). */
export function checkPiiAttempts(input: unknown, sink: IssueSink): void {
  if (!isPlainObject(input)) return;
  const forms = Array.isArray(input.forms) ? (input.forms as unknown[]) : [];
  forms.forEach((form, fi) => {
    if (!isPlainObject(form)) return;
    const formPath = `$.forms[${String(fi)}]`;
    if (isPlainObject(form.submit) && form.submit.mode !== undefined && form.submit.mode !== "events")
      sink.add(
        "PII_NOT_ALLOWED",
        `${formPath}.submit.mode`,
        "Form response collection is disabled in initial V1; only bounded choice events exist.",
        {}
      );
    const fields = Array.isArray(form.fields) ? (form.fields as unknown[]) : [];
    fields.forEach((field, i) => {
      if (!isPlainObject(field)) return;
      const p = `${formPath}.fields[${String(i)}]`;
      if (typeof field.control === "string" && PII_CONTROLS.has(field.control.toLowerCase()))
        sink.add("PII_NOT_ALLOWED", `${p}.control`, "Personal-data controls are not available in initial V1.", {});
      if (field.dataClass !== undefined && field.dataClass !== "choice" && field.dataClass !== "local_only")
        sink.add(
          "PII_NOT_ALLOWED",
          `${p}.dataClass`,
          "Only choice and local_only data classes exist in initial V1.",
          {}
        );
      if (isPlainObject(field.validation) && field.validation.format !== undefined)
        sink.add(
          "PII_NOT_ALLOWED",
          `${p}.validation.format`,
          "Contact-data validators are not available in initial V1.",
          {}
        );
    });
  });
  const survey = input.survey;
  if (!isPlainObject(survey) || !Array.isArray(survey.questions)) return;
  (survey.questions as unknown[]).forEach((q, i) => {
    if (!isPlainObject(q)) return;
    const p = `$.survey.questions[${String(i)}]`;
    if (typeof q.kind === "string" && FREE_TEXT_SURVEY_KINDS.has(q.kind.toLowerCase()))
      sink.add("PII_NOT_ALLOWED", `${p}.kind`, "Free-text survey questions are not available in initial V1.", {});
    for (const key of FREE_TEXT_OPTION_KEYS) {
      if (Object.hasOwn(q, key))
        sink.add("PII_NOT_ALLOWED", childPath(p, key), "Free-text answers are not available in initial V1.", {});
    }
    if (Array.isArray(q.options))
      (q.options as unknown[]).forEach((o, j) => {
        if (!isPlainObject(o)) return;
        for (const key of FREE_TEXT_OPTION_KEYS)
          if (Object.hasOwn(o, key))
            sink.add(
              "PII_NOT_ALLOWED",
              childPath(`${p}.options[${String(j)}]`, key),
              "Free-text answers are not available in initial V1.",
              {}
            );
      });
  });
}
